import React from 'react'
import Plot from '~/prix/react/components/plot'
import TimeSeriesPreview from '~/components/timeSeriesPreview'
import { Wrapper } from '../styles'
import { addDays, format, parse, parseISO, isValid, subDays, subWeeks } from 'date-fns'
import { stripParens, formatVisualizationValue, resolveVisualizationUnitFormat } from '../utils/format'
import { axisBase, buildDayOfYearTicks, buildXTicks } from '../utils/axis'
import { buildBandShapes } from '../utils/bands'
import {
  looksLikeMonthOffsets,
  monthLabelsFromOffsets,
  normalizeWeekLabels,
  buildWeekTicks,
} from '../utils/mad'

import {
  TimeSeriesWithBandsProps,
  TimeSeriesWithBandsPayload,
  SeriesInput,
  SeriesNormalized,
  MadBands,
  PeriodType,
  ValueRulesMeta,
  Visualization,
} from '../visualization.types'
import { getAlertColor } from '~/packages/variable/spaceTime.data'

// Detecta a compressão vertical pelos bands
function isCompressedByBands(
  series: Array<{ values: number[] }>,
  bands?: { lower?: number; upper?: number },
  threshold = 8,
) {
  const maxY = Math.max(1, ...series.flatMap(s => s.values ?? [0]))
  const bandSpan =
    typeof bands?.upper === 'number' && typeof bands?.lower === 'number'
      ? Math.max(1, Math.abs(bands.upper - bands.lower))
      : Math.max(1, maxY)
  return maxY / bandSpan >= threshold
}

function isVisualizationPayload(
  visualization?: Visualization<TimeSeriesWithBandsPayload> | TimeSeriesWithBandsPayload,
): visualization is Visualization<TimeSeriesWithBandsPayload> {
  return Boolean(
    visualization && (visualization as Visualization<TimeSeriesWithBandsPayload>).renderer,
  )
}

type NormalizedValueRules = {
  minValue?: number
}

function normalizeValueRules(valueRules?: ValueRulesMeta): NormalizedValueRules | undefined {
  if (!valueRules) return undefined

  const minValue =
    typeof valueRules.minValue === 'number' && Number.isFinite(valueRules.minValue)
      ? valueRules.minValue
      : undefined

  if (minValue === undefined) {
    return undefined
  }

  return { minValue }
}

function applyValueRules(value: number, valueRules?: NormalizedValueRules): number {
  if (!Number.isFinite(value) || !valueRules) {
    return value
  }

  let bounded = value

  if (typeof valueRules.minValue === 'number') {
    bounded = Math.max(bounded, valueRules.minValue)
  }

  return Object.is(bounded, -0) ? 0 : bounded
}

function normalizeBandsWithValueRules(
  bands: MadBands | undefined,
  valueRules?: NormalizedValueRules,
): MadBands | undefined {
  if (!bands || !valueRules) return bands

  const normalized = { ...bands } as Record<string, any>
  let lower = typeof normalized.lower === 'number' ? normalized.lower : undefined
  let upper = typeof normalized.upper === 'number' ? normalized.upper : undefined

  if (typeof lower === 'number' && typeof upper === 'number') {
    // Regra de negócio: só limita inferior quando ficar abaixo do mínimo.
    if (typeof valueRules.minValue === 'number') {
      lower = Math.max(lower, valueRules.minValue)
    }

    // Superior permanece inalterado. Evita inversão de faixa.
    if (upper < lower) {
      upper = lower
    }

    normalized.lower = lower
    normalized.upper = upper
  } else {
    if (typeof lower === 'number') normalized.lower = applyValueRules(lower, valueRules)
    if (typeof upper === 'number') normalized.upper = applyValueRules(upper, valueRules)
  }

  if (typeof normalized.median === 'number') {
    const boundedMedian = (() => {
      const median = normalized.median as number
      if (typeof normalized.lower === 'number' && typeof normalized.upper === 'number') {
        return Math.max(normalized.lower, Math.min(median, normalized.upper))
      }
      return applyValueRules(median, valueRules)
    })()
    normalized.median = boundedMedian
  }

  if (normalized.shaded && typeof normalized.shaded === 'object') {
    const shaded = { ...normalized.shaded } as Record<string, any>
    if (typeof normalized.lower === 'number' && typeof normalized.upper === 'number') {
      shaded.from = normalized.lower
      shaded.to = normalized.upper
    } else {
      if (typeof shaded.from === 'number') shaded.from = applyValueRules(shaded.from, valueRules)
      if (typeof shaded.to === 'number') shaded.to = applyValueRules(shaded.to, valueRules)
    }
    if (
      typeof shaded.from === 'number' &&
      typeof shaded.to === 'number' &&
      shaded.from > shaded.to
    ) {
      const swap = shaded.from
      shaded.from = shaded.to
      shaded.to = swap
    }
    normalized.shaded = shaded
  }

  return normalized
}

export default function TimeSeriesWithBands(props: TimeSeriesWithBandsProps) {
  const visualization = props.visualization
  const isExpanded: boolean = !!props.isExpanded
  const isMaximized: boolean = !!props.isMaximized
  const alertColor = props.alert ? getAlertColor(props.alert) : undefined

  const payload: TimeSeriesWithBandsPayload | undefined =
    (isVisualizationPayload(visualization) ? visualization.payload : undefined) ??
    props.payload ??
    (!isVisualizationPayload(visualization) && visualization
      ? (visualization as TimeSeriesWithBandsPayload)
      : undefined)

  if (!payload) {
    console.warn('TimeSeriesWithBands: payload ausente', { props })
    return null
  }

  const chartHeight = !isExpanded ? 60 : isMaximized ? 250 : 205

  const rawSeries: SeriesInput[] = Array.isArray(payload.series) ? payload.series : []
  const bands: MadBands | undefined = payload.bands
  const valueRules = normalizeValueRules(payload?.meta?.valueRules)
  const displayBands = normalizeBandsWithValueRules(bands, valueRules)
  const forceYAxisToZero = valueRules?.minValue === 0
  const periodType: PeriodType | undefined = payload?.meta?.periodType ?? payload?.periodType
  const visualizationUnitFormat = resolveVisualizationUnitFormat(payload?.meta)

  const series: SeriesNormalized[] = rawSeries.map((s, i) => ({
    name: stripParens(s.name ?? s.label) ?? `Série ${i + 1}`,
    color: s.color ?? '#2563eb',
    fillColor: s.fillColor,
    values: (s.y ?? []) as number[],
    labelRaw: (s.label ?? s.name ?? '') as string,
    year: s.year,
  }))

  if (!isExpanded) {
    return (
      <Wrapper $height={chartHeight}>
        <TimeSeriesPreview series={series} bands={displayBands as any} disableYAxis disablePoints />
      </Wrapper>
    )
  }

  const refStart: string =
    payload?.reference?.start ||
    payload?.meta?.reference?.start ||
    new Date().toISOString().slice(0, 10)
  const refEnd: string | undefined =
    payload?.reference?.end || payload?.meta?.reference?.end || undefined

  const parseDateLoose = (value?: string) => {
    if (!value) return null
    try {
      const isoCandidate = parseISO(value)
      if (isValid(isoCandidate)) return isoCandidate
      const brCandidate = parse(value, 'dd/MM/yyyy', new Date())
      if (isValid(brCandidate)) return brCandidate
      return null
    } catch {
      return null
    }
  }

  // MonthlyMAD
  const xOffsets: number[] | undefined = Array.isArray(payload.x)
    ? (payload.x as number[])
    : undefined

  const isMonthlyMAD =
    periodType === 'month' && Array.isArray(xOffsets) && looksLikeMonthOffsets(xOffsets)

  if (isMonthlyMAD) {
    const pairs = xOffsets
      .map((off, idx) => ({ off, idx }))
      .filter(p => p.off <= 0)
      .slice(-12)
    const xs = pairs.map((_, i) => i + 1)
    const labels = monthLabelsFromOffsets(
      refStart,
      pairs.map(p => p.off),
    )
    const monthTickText = labels.map(label => label.split('/')[0] ?? label)

    const data: Partial<Plotly.PlotData>[] = series.flatMap(s => {
      const ys = pairs.map(p => s.values[p.idx])
      const n = ys.length

      const hoverAll = ys.map(
        (yv: number, i: number) =>
          `Período: ${labels[i]}<br>Valor: ${formatVisualizationValue(yv, visualizationUnitFormat)}`,
      )
      const hoverLast = `Período: ${labels[n - 1]}<br>Valor: ${formatVisualizationValue(
        ys[n - 1],
        visualizationUnitFormat,
      )}`

      const pastLine: Partial<Plotly.PlotData> = {
        x: xs,
        y: ys,
        type: 'scatter',
        mode: 'lines+markers',
        line: { color: '#9ca3af', width: 3 },
        marker: { size: 6, color: '#9ca3af' },
        name: s.name,
        cliponaxis: true,
        hovertext: hoverAll,
        hovertemplate: '%{hovertext}<extra></extra>',
        showlegend: false,
      }

      const lastPoint: Partial<Plotly.PlotData> = {
        x: [xs[n - 1]],
        y: [ys[n - 1]],
        type: 'scatter',
        mode: 'markers',
        marker: {
          size: 12,
          color: alertColor ?? s.color ?? '#2563eb',
          line: { width: 2, color: '#ffffff' },
        },
        name: `${s.name} (atual)`,
        hovertext: [hoverLast],
        hovertemplate: '%{hovertext}<extra></extra>',
        showlegend: false,
      }

      return [pastLine, lastPoint]
    })

    const compressed = isCompressedByBands(
      series,
      displayBands as { lower?: number; upper?: number },
      8,
    )
    const { shapes, annotations } = buildBandShapes(displayBands as any, {
      nudgeLabels: compressed,
    }) as {
      shapes: Partial<Plotly.Shape>[]
      annotations: Partial<Plotly.Annotations>[]
    }

    const layout: Partial<Plotly.Layout> = {
      height: chartHeight,
      margin: { l: 48, r: 64, t: 25, b: 0 },
      paper_bgcolor: 'white',
      plot_bgcolor: 'white',
      showlegend: false,
      xaxis: {
        ...axisBase,
        showgrid: false,
        autorange: false,
        range: [0.5, xs.length + 0.5],
        tickmode: 'array',
        tickvals: xs,
        ticktext: monthTickText,
        tickangle: 0,
        title: '',
        color: '#000',
        tickfont: { size: 11, color: '#000', family: 'Figtree, sans-serif' },
      },
      yaxis: {
        ...axisBase,
        title: '',
        nticks: 4,
        tickformat: '~s',
        zeroline: true,
        zerolinecolor: '#c5c4c4',
        zerolinewidth: 1.25,
        ...(forceYAxisToZero ? { rangemode: 'tozero' } : {}),
        color: '#000',
        tickfont: { size: 11, color: '#000', family: 'Figtree, sans-serif' },
      },
      hovermode: 'closest',
      hoverlabel: {
        align: 'left',
        bgcolor: 'white',
        bordercolor: '#e5e7eb',
        font: { size: 13, color: '#111827', family: 'Figtree, sans-serif' },
      },
      shapes,
      annotations,
    }

    return (
      <Wrapper $height={chartHeight}>
        <Plot data={data as Plotly.Data[]} layout={layout as Plotly.Layout} />
      </Wrapper>
    )
  }

  // WeeklyMAD
  const isWeeklyMAD =
    periodType === 'week' && (Array.isArray(payload.weekLabels) || Array.isArray(payload.labels))

  if (isWeeklyMAD) {
    const rawLabels: unknown[] = payload.weekLabels ?? payload.labels ?? []
    const labels = normalizeWeekLabels(rawLabels, { withYear: true, refStart })
    const n = labels.length
    const xs = Array.from({ length: n }, (_, i) => i + 1)

    const data: Partial<Plotly.PlotData>[] = series.flatMap(s => {
      const ys = (s.values ?? []).slice(0, n)
      const hoverAll = ys.map(
        (yv: number, i: number) =>
          `Semana ${xs[i]} (${labels[i]})<br>Valor: ${formatVisualizationValue(
            yv,
            visualizationUnitFormat,
          )}`,
      )
      const lastIdx = n - 1

      const pastLine: Partial<Plotly.PlotData> = {
        x: xs,
        y: ys,
        type: 'scatter',
        mode: 'lines+markers',
        line: { color: '#9ca3af', width: 3 },
        marker: { size: 6, color: '#9ca3af' },
        name: s.name,
        cliponaxis: true,
        hovertext: hoverAll,
        hovertemplate: '%{hovertext}<extra></extra>',
        showlegend: false,
      }

      const lastPoint: Partial<Plotly.PlotData> = {
        x: [xs[lastIdx]],
        y: [ys[lastIdx]],
        type: 'scatter',
        mode: 'markers',
        marker: {
          size: 12,
          color: alertColor ?? s.color ?? '#2563eb',
          line: { width: 2, color: '#ffffff' },
        },
        name: `${s.name} (atual)`,
        hovertext: [
          `Semana ${xs[lastIdx]} (${labels[lastIdx]})<br>Valor: ${formatVisualizationValue(
            ys[lastIdx],
            visualizationUnitFormat,
          )}`,
        ],
        hovertemplate: '%{hovertext}<extra></extra>',
        showlegend: false,
      }

      return [pastLine, lastPoint]
    })

    const { tickvals, ticktext } = buildWeekTicks(n, labels)

    const compressed = isCompressedByBands(
      series,
      displayBands as { lower?: number; upper?: number },
      8,
    )
    const { shapes, annotations } = buildBandShapes(displayBands as any, {
      nudgeLabels: compressed,
    }) as {
      shapes: Partial<Plotly.Shape>[]
      annotations: Partial<Plotly.Annotations>[]
    }

    const layout: Partial<Plotly.Layout> = {
      height: chartHeight,
      margin: { l: 48, r: 64, t: 25, b: 0 },
      paper_bgcolor: 'white',
      plot_bgcolor: 'white',
      showlegend: false,
      xaxis: {
        ...axisBase,
        showgrid: false,
        autorange: true,
        tickmode: 'array',
        tickvals,
        ticktext,
        tickangle: 0,
        title: '',
        color: '#000',
        tickfont: { size: 11, color: '#000', family: 'Figtree, sans-serif' },
      },
      yaxis: {
        ...axisBase,
        title: '',
        nticks: 4,
        tickformat: '~s',
        zeroline: true,
        zerolinecolor: '#c5c4c4',
        zerolinewidth: 1.25,
        ...(forceYAxisToZero ? { rangemode: 'tozero' } : {}),
        color: '#000',
        tickfont: { size: 11, color: '#000', family: 'Figtree, sans-serif' },
      },
      hovermode: 'closest',
      hoverlabel: {
        align: 'left',
        bgcolor: 'white',
        bordercolor: '#e5e7eb',
        font: { size: 13, color: '#111827', family: 'Figtree, sans-serif' },
      },
      shapes,
      annotations,
    }

    return (
      <Wrapper $height={chartHeight}>
        <Plot data={data as Plotly.Data[]} layout={layout as Plotly.Layout} />
      </Wrapper>
    )
  }

  // DailyMad Fallback
  const longest = Math.max(...series.map(s => s.values?.length ?? 0)) || 1
  const x = Array.from({ length: longest }, (_, i) => i + 1)

  // flags do tipo de série neste fallback
  const isWeeklyFallback = periodType === 'week'
  const isDailyFallback =
    periodType === 'day' || periodType === 'year' || (!isWeeklyFallback && longest >= 120)

  // ticks do eixo X
  const { vals: xTickVals, texts: xTickTexts } = isDailyFallback
    ? buildDayOfYearTicks(longest, { minGap: 30 })
    : buildXTicks(longest)

  const fallbackWeeklyEnd = parseDateLoose(refEnd) || parseDateLoose(refStart)
  const fallbackDailyEnd = fallbackWeeklyEnd

  const data: Partial<Plotly.PlotData>[] = series.flatMap(s => {
    const ys = s.values ?? []
    const lastIdx = ys.length - 1

    const hoverAll = ys.map((yv: number, idx: number) => {
      if (isWeeklyFallback) {
        if (fallbackWeeklyEnd) {
          const weeksBack = x.length - 1 - idx
          const end = subWeeks(fallbackWeeklyEnd, weeksBack)
          const start = addDays(end, -6)
          return `Semana ${x[idx]} (${format(start, 'dd/MM/yy')} - ${format(
            end,
            'dd/MM/yy',
          )})<br>Valor: ${formatVisualizationValue(yv, visualizationUnitFormat)}`
        }
        return `Semana ${x[idx]}<br>Valor: ${formatVisualizationValue(yv, visualizationUnitFormat)}`
      }
      if (isDailyFallback) {
        if (fallbackDailyEnd) {
          const daysBack = x.length - 1 - idx
          const day = subDays(fallbackDailyEnd, daysBack)
          return `Dia ${format(day, 'dd/MM/yy')}<br>Valor: ${formatVisualizationValue(
            yv,
            visualizationUnitFormat,
          )}`
        }
        return `Dia ${x[idx]}<br>Valor: ${formatVisualizationValue(yv, visualizationUnitFormat)}`
      }
      return `${s.name}<br>Valor: ${formatVisualizationValue(yv, visualizationUnitFormat)}`
    })

    const pastLine: Partial<Plotly.PlotData> = {
      x,
      y: ys,
      type: 'scatter',
      mode: 'lines+markers',
      line: { color: '#9ca3af', width: 3 },
      marker: { size: 6, color: '#9ca3af' },
      name: s.name,
      cliponaxis: true,
      hovertext: hoverAll,
      hovertemplate: '%{hovertext}<extra></extra>',
      showlegend: false,
    }

    const lastPoint: Partial<Plotly.PlotData> | null =
      lastIdx >= 0
        ? {
            x: [x[lastIdx]],
            y: [ys[lastIdx]],
            type: 'scatter',
            mode: 'markers',
            marker: {
              size: 12,
              color: alertColor ?? s.color ?? '#2563eb',
              line: { width: 2, color: '#fff' },
            },
            name: `${s.name} (atual)`,
            hovertext: [hoverAll[lastIdx]],
            hovertemplate: '%{hovertext}<extra></extra>',
            showlegend: false,
          }
        : null

    const traces: Partial<Plotly.PlotData>[] = [pastLine]
    if (lastPoint) traces.push(lastPoint)

    return traces
  })

  const compressed = isCompressedByBands(
    series,
    displayBands as { lower?: number; upper?: number },
    4,
  )
  const { shapes, annotations } = buildBandShapes(displayBands as any, {
    nudgeLabels: compressed,
  }) as {
    shapes: Partial<Plotly.Shape>[]
    annotations: Partial<Plotly.Annotations>[]
  }

  const layout: Partial<Plotly.Layout> = {
    height: chartHeight,
    margin: { l: 48, r: 64, t: 28, b: 6 },
    paper_bgcolor: 'white',
    plot_bgcolor: 'white',
    showlegend: series.length > 1,
    xaxis: {
      ...axisBase,
      showgrid: false,
      autorange: true,
      tickmode: 'array',
      tickvals: xTickVals,
      ticktext: xTickTexts,
      tickangle: 0,
      title: '',
      color: '#000',
      tickfont: { size: 11, color: '#000', family: 'Figtree, sans-serif' },
    },
    yaxis: {
      ...axisBase,
      title: '',
      nticks: 4,
      tickformat: '~s',
      zeroline: true,
      zerolinecolor: '#c5c4c4',
      zerolinewidth: 1.25,
      ...(forceYAxisToZero ? { rangemode: 'tozero' } : {}),
      color: '#000',
      tickfont: { size: 11, color: '#000', family: 'Figtree, sans-serif' },
    },
    hovermode: 'closest',
    hoverlabel: {
      align: 'left',
      bgcolor: 'white',
      bordercolor: '#e5e7eb',
      font: { size: 13, color: '#111827', family: 'Figtree, sans-serif' },
    },
    shapes,
    annotations,
  }

  return (
    <Wrapper $height={chartHeight}>
      <Plot data={data as Plotly.Data[]} layout={layout as Plotly.Layout} />
    </Wrapper>
  )
}
